import {
  Button, Card, ErrorState, Field, Notice, PhoneInput, Stack, normalisePhone, useApi, useI18n,
} from '@daghfal/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/**
 * Moving a person's account to the new number they have now, or taking a number off an account that is no
 * longer theirs (Q25, Q27).
 *
 * Both are for someone the administration has confirmed face to face, and both are decided by the API: it
 * refuses an administrator's account, the administrator's own, and anyone who also belongs to another nursery,
 * with a message saying whom to ask instead.
 */
export function AccountRecoveryPanel({ membershipId, fullName, onClose }: {
  membershipId: number; fullName: string | null; onClose: () => void;
}) {
  const api = useApi();
  const { t, formatPhone, formatDateTime } = useI18n();
  const queryClient = useQueryClient();

  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const renumber = useMutation({
    mutationFn: (newPhone: string) => api.accountRecovery.renumber(membershipId, newPhone),
  });

  const release = useMutation({
    mutationFn: () => api.accountRecovery.release(membershipId),
    // The person's number and badge change on every list that shows them.
    onSuccess: () => queryClient.invalidateQueries(),
  });

  function submitRenumber(event: FormEvent) {
    event.preventDefault();
    const normalised = normalisePhone(phone);
    setPhoneError(normalised ? null : t('auth.phone.required'));
    if (normalised) renumber.mutate(normalised);
  }

  return (
    <Card
      title={t('accountRecovery.title', { name: fullName ?? '—' })}
      action={<Button variant="ghost" onClick={onClose}>{t('action.close')}</Button>}
    >
      <Stack>
        <form onSubmit={submitRenumber} noValidate>
          <Stack>
            <h3>{t('accountRecovery.renumber.title')}</h3>
            <p className="dg-card__hint">{t('accountRecovery.renumber.hint')}</p>
            {renumber.isError && <ErrorState error={renumber.error} />}
            {renumber.isSuccess && (
              <Notice tone="success">
                {t('accountRecovery.renumber.sent', {
                  phone: formatPhone(renumber.data.maskedNewPhone), date: formatDateTime(renumber.data.expiresAt),
                })}
              </Notice>
            )}
            <Field label={t('accountRecovery.renumber.phone')} hint={t('auth.phone.hint')} error={phoneError} required>
              {(props) => <PhoneInput {...props} value={phone} onChange={(event) => setPhone(event.target.value)} />}
            </Field>
            <Button type="submit" variant="secondary" busy={renumber.isPending} busyLabel={t('action.saving')}>
              {t('accountRecovery.renumber.submit')}
            </Button>
          </Stack>
        </form>

        <Stack>
          <h3>{t('accountRecovery.release.title')}</h3>
          <p className="dg-card__hint">{t('accountRecovery.release.hint')}</p>
          {release.isError && <ErrorState error={release.error} />}
          {release.isSuccess
            ? <Notice tone="success">{t('accountRecovery.release.done')}</Notice>
            : (
              <>
                <label className="dg-checkbox">
                  <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
                  <span>{t('accountRecovery.release.confirm')}</span>
                </label>
                <Button
                  variant="danger"
                  disabled={!confirmed}
                  busy={release.isPending}
                  busyLabel={t('action.saving')}
                  onClick={() => release.mutate()}
                >
                  {t('accountRecovery.release.submit')}
                </Button>
              </>
            )}
        </Stack>
      </Stack>
    </Card>
  );
}
